import { useCallback } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSListBox,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { useRefocus } from "@/domains/react/useRefocus";
import { useAppStore } from "@/store";
import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";

export const SettingsMemAgentPage = observer(() => {
  const { store } = useAppStore();
  const { memAgent } = store;

  const handleReload = useCallback(() => {
    void memAgent.reload();
  }, [memAgent]);

  useRefocus(handleReload, { immediate: true });

  const handleSetup = useCallback(async () => {
    await memAgent.setup();
  }, [memAgent]);

  const handleToggleAssistant = useCallback(async () => {
    await memAgent.setAssistantEnabled({
      isEnabled: !memAgent.isAssistantEnabled,
    });
  }, [memAgent]);

  const handleConnectSlack = useCallback(async () => {
    await memAgent.connectSlack();
  }, [memAgent]);

  const handleToggleSlack = useCallback(async () => {
    await memAgent.setSlackEnabled({
      isEnabled: !memAgent.isSlackEnabled,
    });
  }, [memAgent]);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Apps and integrations" title="Mem Agent" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledDescriptionCard>
            <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Primary}>
              Mem Agent is currently in Beta.
            </MDSText>
          </StyledDescriptionCard>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          {!memAgent.hasAssistant ? (
            <SetupSection
              isLoading={memAgent.isLoading || memAgent.isSettingUp}
              onSetup={handleSetup}
            />
          ) : (
            <AgentSettingsSection
              assistantSlug={memAgent.assistantSlug}
              isAssistantEnabled={memAgent.isAssistantEnabled}
              isUpdatingAssistant={memAgent.isUpdatingAssistant}
              onToggleAssistant={handleToggleAssistant}
            />
          )}
        </SettingsIntegrationSection>

        {memAgent.hasAssistant && (
          <SettingsIntegrationSection>
            <SlackSettingsSection
              isSlackConnected={memAgent.isSlackConnected}
              isSlackEnabled={memAgent.isSlackEnabled}
              isConnectingSlack={memAgent.isConnectingSlack}
              isUpdatingSlack={memAgent.isUpdatingSlack}
              slackTeamName={memAgent.slackTeamName}
              onConnectSlack={handleConnectSlack}
              onToggleSlack={handleToggleSlack}
            />
          </SettingsIntegrationSection>
        )}
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

function SetupSection({ isLoading, onSetup }: { isLoading: boolean; onSetup: () => void }) {
  return (
    <StyledCard>
      <StyledCardCopy>
        <MDSText variant={MDSTextVariant.TextS}>Mem Agent is not set up yet.</MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
          Set up your agent to enable agent controls and connect Slack.
        </MDSText>
      </StyledCardCopy>
      <MDSButton onPress={onSetup} isLoading={isLoading}>
        <MDSIcon kind={MDSIconKind.Robot} slot={MDSButtonSlotName.LeftIcon} />
        Set up Mem Agent
      </MDSButton>
    </StyledCard>
  );
}

function AgentSettingsSection({
  assistantSlug,
  isAssistantEnabled,
  isUpdatingAssistant,
  onToggleAssistant,
}: {
  assistantSlug: string | null;
  isAssistantEnabled: boolean;
  isUpdatingAssistant: boolean;
  onToggleAssistant: () => void;
}) {
  const statusLabel = isAssistantEnabled ? "Enabled" : "Disabled";
  const buttonLabel = isAssistantEnabled ? "Disable" : "Enable";

  return (
    <MDSListBox
      aria-label="Mem Agent settings"
      items={[
        {
          id: "mem-agent-status",
          children: (
            <StyledListItemText>
              <StyledStatusRow>
                <MDSText variant={MDSTextVariant.TextS}>Your agent</MDSText>
                {isAssistantEnabled && (
                  <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
                )}
              </StyledStatusRow>
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                {assistantSlug ? `${assistantSlug} · ${statusLabel}` : statusLabel}
              </MDSText>
            </StyledListItemText>
          ),
          rightAddon: (
            <MDSButton onPress={onToggleAssistant} isLoading={isUpdatingAssistant}>
              {buttonLabel}
            </MDSButton>
          ),
        },
      ]}
    />
  );
}

function SlackSettingsSection({
  isSlackConnected,
  isSlackEnabled,
  isConnectingSlack,
  isUpdatingSlack,
  slackTeamName,
  onConnectSlack,
  onToggleSlack,
}: {
  isSlackConnected: boolean;
  isSlackEnabled: boolean;
  isConnectingSlack: boolean;
  isUpdatingSlack: boolean;
  slackTeamName: string | null;
  onConnectSlack: () => void;
  onToggleSlack: () => void;
}) {
  const statusLabel = (() => {
    if (!isSlackConnected) return "Not connected";
    if (!isSlackEnabled) return "Connected, disabled";
    return slackTeamName ? `Connected to ${slackTeamName}` : "Connected";
  })();

  const action = isSlackConnected ? (
    <MDSButton onPress={onToggleSlack} isLoading={isUpdatingSlack}>
      {isSlackEnabled ? "Disable" : "Enable"}
    </MDSButton>
  ) : (
    <MDSButton onPress={onConnectSlack} isLoading={isConnectingSlack}>
      <MDSIcon kind={MDSIconKind.Message} slot={MDSButtonSlotName.LeftIcon} />
      Connect Slack
    </MDSButton>
  );

  return (
    <StyledSectionStack>
      <MDSText variant={MDSTextVariant.TextS}>Slack</MDSText>
      <MDSListBox
        aria-label="Mem Agent Slack settings"
        items={[
          {
            id: "mem-agent-slack-status",
            children: (
              <StyledListItemText>
                <StyledStatusRow>
                  <MDSText variant={MDSTextVariant.TextS}>Slack connection</MDSText>
                  {isSlackConnected && isSlackEnabled && (
                    <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
                  )}
                </StyledStatusRow>
                <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                  {statusLabel}
                </MDSText>
              </StyledListItemText>
            ),
            rightAddon: action,
          },
        ]}
      />
    </StyledSectionStack>
  );
}

const StyledDescriptionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.background.card.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing[5]};
  max-width: 700px;
`;

const StyledCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background.card.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing[5]};
  max-width: 700px;

  @container mds-panel (max-width: 750px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const StyledCardCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledSectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledListItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;
